import { useInfiniteQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardApi, IssuesExportResult } from "../../../services/board-service-api";
import useLoadingPresentation, { type LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import BoardAvatar from "./board-avatar";
import { BoardService } from "./board-service";
import { displayNameOf, usePeopleDirectory } from "./people-directory";
import "./project-issues.scss";
import TypeIcon from "./type-icon";
import {
  BoardChangeEvent,
  BoardStatus,
  IssueCard,
  IssuePage,
  PRIORITY_COLOR,
  PRIORITY_LABELS,
  StatusCategory,
  STATUS_DOT_COLOR
} from "./types";
import { useBoardConfig } from "./use-board";

interface ProjectIssuesProps {
  projectKey: string;
}

interface StatusInfo {
  name: string;
  category: StatusCategory;
}

// The project's issue navigator: every issue in the project across all statuses/boards, with free
// text search and infinite paging. Each row opens the issue's full page.
const ProjectIssues: React.FC<ProjectIssuesProps> = ({ projectKey }) => {
  const { config } = useBoardConfig();
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();
  // Resolve assignee names: load the people directory and re-render when it lands. Without this the
  // tab is stuck on the short-id fallback (it has no subscribing ancestor like the board page does).
  usePeopleDirectory();
  const [q, setQ] = useState<string>("");
  const [debouncedQ, setDebouncedQ] = useState<string>("");
  const [exporting, setExporting] = useState<boolean>(false);

  useEffect(() => {
    const handle: ReturnType<typeof setTimeout> = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(handle);
  }, [q]);

  // CSV export — fetch the server-built CSV and trigger a browser download.
  const exportCsv = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const result: IssuesExportResult = await BoardApi.exportIssues(projectKey);
      const url: string = URL.createObjectURL(new Blob([result.csv], { type: "text/csv;charset=utf-8" }));
      const link: HTMLAnchorElement = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      // toasted by the api layer
    } finally {
      setExporting(false);
    }
  };

  const query = useInfiniteQuery({
    queryKey: ["board", "projectIssues", projectKey, debouncedQ],
    queryFn: ({ pageParam = null }: { pageParam?: string | null }): Promise<IssuePage> =>
      BoardApi.queryProjectIssues(projectKey, { q: debouncedQ || undefined, cursor: pageParam, limit: 50 }),
    getNextPageParam: (lastPage: IssuePage): string | undefined => lastPage.nextCursor ?? undefined
  });

  // Refetch when issues in this project change (created/moved/edited/deleted).
  useEffect(() => {
    return BoardService.subscribe((event: BoardChangeEvent) => {
      if (event.kind.startsWith("issue.")) void query.refetch();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch identity is stable enough here
  }, [projectKey, debouncedQ]);

  const items: IssueCard[] = useMemo(
    () => (query.data ? query.data.pages.flatMap((page: IssuePage) => page.items) : []),
    [query.data]
  );
  const total: number = query.data?.pages[0]?.total ?? 0;
  // The first fetch of a search draws the table's rows after a short wait; a fast answer never
  // flashes them (L7).
  const presentation: LoadingPresentation = useLoadingPresentation({
    isLoading: query.isLoading,
    hasData: items.length > 0
  });
  // While the skeleton serves out its minimum time the rows may already be here; they wait for it.
  const isShowingContent: boolean = presentation === "content" || presentation === "refreshing";

  const statusInfo = (statusId: string): StatusInfo => {
    const status: BoardStatus | undefined = config.statuses.find((entry: BoardStatus) => entry.id === statusId);
    return { name: status?.name ?? statusId, category: status?.category ?? "todo" };
  };

  return (
    <div className="kuika-project-issues">
      <div className="kuika-project-issues__bar">
        <KuikaInput
          size="sm"
          style={{ width: 260 }}
          value={q}
          placeholder="Search issues…"
          onChange={(_event, value: string | number) => setQ(String(value))}
        />
        <span className="kuika-project-issues__count">{total} issues</span>
        <span style={{ flex: 1 }} />
        <KuikaButton type="secondary" size="sm" loading={exporting} onClick={() => void exportCsv()}>
          <KuikaSvg name="file-export" width={13} height={13} />
          <span style={{ marginLeft: 5 }}>Export CSV</span>
        </KuikaButton>
      </div>

      {presentation === "skeleton" && (
        <KuikaSkeletonRegion label="Loading issues" className="kuika-project-issues__loading">
          <KuikaSkeletonList rows={8} density="comfortable" />
        </KuikaSkeletonRegion>
      )}
      {isShowingContent && items.length === 0 && <div className="kuika-project-issues__empty">No issues match.</div>}
      {isShowingContent && items.length > 0 && (
        <table className="kuika-project-issues__table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Summary</th>
              <th>Status</th>
              <th>Assignee</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {items.map((issue: IssueCard) => {
              const status: StatusInfo = statusInfo(issue.statusId);
              return (
                <tr key={issue.id} onClick={() => navigate(`/${domainName}/issue/${issue.key}`)}>
                  <td className="kuika-project-issues__key">{issue.key}</td>
                  <td>
                    <span className="kuika-project-issues__summary-cell">
                      <TypeIcon type={issue.type} />
                      <span className="kuika-project-issues__summary">{issue.summary}</span>
                    </span>
                  </td>
                  <td>
                    <span className="kuika-project-issues__chip">
                      <span
                        className="kuika-project-issues__dot"
                        style={{ background: STATUS_DOT_COLOR[status.category] }}
                      />
                      {status.name}
                    </span>
                  </td>
                  <td>
                    {issue.assigneeUserId ? (
                      <span className="kuika-project-issues__assignee">
                        <BoardAvatar userId={issue.assigneeUserId} className="kuika-board__assignee-avatar" />
                        {displayNameOf(issue.assigneeUserId)}
                      </span>
                    ) : (
                      <span className="kuika-project-issues__muted">Unassigned</span>
                    )}
                  </td>
                  <td>
                    <span className="kuika-project-issues__chip">
                      <span
                        className="kuika-project-issues__dot"
                        style={{ background: PRIORITY_COLOR[issue.priority] }}
                      />
                      {PRIORITY_LABELS[issue.priority]}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {query.hasNextPage && (
        <div className="kuika-project-issues__more">
          <KuikaButton
            type="secondary"
            size="sm"
            loading={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            Load more
          </KuikaButton>
        </div>
      )}
    </div>
  );
};

export default ProjectIssues;
